import React, { Component } from 'react';
import { flattenDeep } from 'lodash';

// Redux
import developers from 'data/developers';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  removeProjectDeveloper,
  updateProjectTitle,
  newProjectDeveloper,
  reorderProjectWeek,
  reorderProjects,
  moveDeveloper,
} from 'actions/projects';
import { updateHeaderTitle } from 'actions/headers';

// Components
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import DeveloperPicker from 'components/DeveloperPicker';
import AddProjectButton from 'components/AddProjectButton';
import DeveloperCard from 'components/DeveloperCard';
import NoteModal from 'components/NoteModal';

// a little function to help us with reordering the result
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const getItemStyle = (isDragging, draggableStyle) => ({
  userSelect: 'none',
  alignItems: 'center',
  justifyContent: 'center',
  margin: 5,
  ...draggableStyle,
});

class Board extends Component {
  handleProjectDrop = result => {
    const { source, destination } = result;
    if (destination === null) {
      return;
    }
    if (source.index === destination.index) {
      return;
    }

    const orderedProjects = reorder(
      this.props.projects,
      source.index,
      destination.index,
    );

    this.props.reorderProjects(orderedProjects);
  };

  onDragEnd = result => {
    const { source, destination } = result;
    const isNew = source.index === true;

    if (source.droppableId === 'projectDroppable') {
      return this.handleProjectDrop(result);
    }

    // dropped outside the list and was new
    if (!destination && isNew) {
      return;
    }

    // Create one
    if (isNew) {
      const weekId = destination.droppableId;
      let developer = developers.find(d => d.id === result.draggableId);
      this.props.newProjectDeveloper(weekId, developer, destination.index);
      return;
    }

    // Delete one
    if (!destination) {
      this.props.removeProjectDeveloper(result.draggableId);
      return;
    }

    // Re-order
    const weeks = flattenDeep(this.props.projects.map(p => p.weeks));
    const sourceWeek = weeks.find(w => w.id === source.droppableId);
    if (source.droppableId === destination.droppableId) {
      const orderedDevelopers = reorder(
        sourceWeek.developers,
        source.index,
        destination.index,
      );

      this.props.reorderProjectWeek(sourceWeek.id, orderedDevelopers);
      return;
    }

    // Move to another project
    if (source.droppableId !== destination.droppableId) {
      const destinationWeek = weeks.find(w => w.id === destination.droppableId);
      const developer = sourceWeek.developers.find(
        d => d.id === result.draggableId,
      );

      this.props.moveDeveloper(
        sourceWeek.id,
        destinationWeek.id,
        developer,
        destination.index,
      );
    }
  };

  renderEmptyMessage = () => {
    if (!this.props.projects.length) {
      return (
        <p style={{ fontSize: '22px', padding: '30px', fontWeight: 'bold' }}>
          There are no projects
        </p>
      );
    }
  };

  renderHeaders = () => {
    return (
      <div
        data-id="project-list"
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-evenly',
          overflowX: 'overlay',
        }}
      >
        <div
          style={{
            flex: 1,
            maxWidth: 378,
            flexBasis: 'auto',
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderBottom: '3px solid #666',
            borderRight: '3px solid #666',
          }}
        >
          <input disabled style={styles.input} />
        </div>
        {this.props.headers.map(h => (
          <div key={h.id} className="weekList" style={styles.weekList}>
            <input
              placeholder={'Jul 16'}
              onChange={e => {
                e.preventDefault();
                this.props.updateHeaderTitle(h.id, e.target.value);
              }}
              value={h.title}
              style={styles.headerInput}
            />
          </div>
        ))}
      </div>
    );
  };

  render() {
    return (
      <div>
        {this.props.projects.length > 0 && this.renderHeaders()}
        <DragDropContext onDragEnd={this.onDragEnd}>
          <Droppable
            type="project"
            droppableId="projectDroppable"
            direction="vertical"
          >
            {(provided, snapshot) => (
              <div ref={provided.innerRef}>
                {this.props.projects.map((project, projectIndex) => (
                  <Draggable
                    key={project.id}
                    type="project"
                    draggableId={project.id}
                    index={projectIndex}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        key={project.id}
                        title={project.title}
                        data-id="project-list"
                        style={{
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-evenly',
                          backgroundColor: project.title.includes('*')
                            ? 'rgb(199, 199, 199)'
                            : 'transparent',
                          ...provided.draggableProps.style,
                          ...(snapshot.isDragging
                            ? {
                                backgroundColor: '#10ca97AA',
                              }
                            : {}),
                        }}
                      >
                        <div
                          style={{
                            flex: 1,
                            maxWidth: 378,
                            flexBasis: 'auto',
                            flexGrow: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderBottom: '3px solid #666',
                            borderRight: '3px solid #666',
                          }}
                        >
                          <div
                            {...provided.dragHandleProps}
                            style={{
                              paddingLeft: '15px',
                              paddingTop: '15px',
                              paddingBottom: '15px',
                            }}
                          >
                            <span aria-label="move project" role="img">
                              ↕️
                            </span>
                          </div>
                          <input
                            value={project.title}
                            onChange={e => {
                              e.preventDefault();
                              this.props.updateProjectTitle(
                                project.id,
                                e.target.value,
                              );
                            }}
                            title={project.title}
                            style={styles.input}
                          />
                        </div>
                        {project.weeks.map(week => (
                          <Droppable
                            key={week.id}
                            droppableId={week.id}
                            direction="horizontal"
                          >
                            {(provided, snapshot) => (
                              <div
                                className="weekList"
                                ref={provided.innerRef}
                                style={styles.weekList}
                              >
                                <NoteModal week={week} />
                                {(week.developers || []).map(
                                  (person, index) => (
                                    <Draggable
                                      key={person.id}
                                      draggableId={person.id}
                                      index={index}
                                    >
                                      {(provided, snapshot) => (
                                        <div
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          {...provided.dragHandleProps}
                                          style={getItemStyle(
                                            snapshot.isDragging,
                                            provided.draggableProps.style,
                                          )}
                                        >
                                          <DeveloperCard
                                            isDragging={snapshot.isDragging}
                                            developer={person}
                                          />
                                        </div>
                                      )}
                                    </Draggable>
                                  ),
                                )}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        ))}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>

          {this.renderEmptyMessage()}
          <AddProjectButton />
          <DeveloperPicker />
        </DragDropContext>
      </div>
    );
  }
}

const styles = {
  input: {
    padding: '5px',
    fontSize: '28px',
    margin: '10px',
    border: 'none',
    background: 'transparent',
    width: '100%',
  },
  headerInput: {
    padding: '5px',
    fontSize: '28px',
    margin: '10px',
    border: 'none',
    background: 'transparent',
    width: '100%',
  },
  weekList: {
    height: '120px',
    padding: 8,
    display: 'flex',
    width: '20%',
    flexShrink: 0,
    flexGrow: 0,
    flex: 1,
    flexDirection: 'row',
    overflowX: 'overlay',
    overflowY: 'hidden',
    alignItems: 'center',
    borderRight: '3px solid #666',
    borderBottom: '3px solid #666',
    position: 'relative',
  },
};

export default connect(
  state => ({
    projects: state.projects,
    headers: state.headers,
  }),
  dispatch => ({
    updateProjectTitle: bindActionCreators(updateProjectTitle, dispatch),
    updateHeaderTitle: bindActionCreators(updateHeaderTitle, dispatch),
    newProjectDeveloper: bindActionCreators(newProjectDeveloper, dispatch),
    removeProjectDeveloper: bindActionCreators(
      removeProjectDeveloper,
      dispatch,
    ),
    reorderProjectWeek: bindActionCreators(reorderProjectWeek, dispatch),
    reorderProjects: bindActionCreators(reorderProjects, dispatch),
    moveDeveloper: bindActionCreators(moveDeveloper, dispatch),
  }),
)(Board);



// WEBPACK FOOTER //
// ./src/Board.js